import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  IconButton,
  Tooltip,
  Avatar,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Menu,
  MenuItem,
  ListItemIcon as MenuItemIcon,
  Card,
  CardContent,
  CardActionArea,
  ListItemText,
  List,
  ListItem,
  CircularProgress,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AutoStoriesIcon from '@mui/icons-material/AutoStories';
import EditIcon from '@mui/icons-material/Edit';
import DashboardIcon from '@mui/icons-material/Dashboard';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import DeleteIcon from '@mui/icons-material/Delete';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import Grow from '@mui/material/Grow';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { motion, AnimatePresence } from 'framer-motion';
import apiClient from '../utils/apiInterceptor';
import '../styles/nexusTheme.css';
import './Workspace.css';
import './ToolsPage.css';

/** 与后端 config.assistAi.dailyLimit 一致（公测每日免费次数） */
const ASSIST_AI_DAILY_LIMIT = 50;

function normalizeAssistQuotaPayload(raw) {
  const body = raw?.data && typeof raw.data === 'object' && !Array.isArray(raw.data) ? raw.data : raw;
  const dailyLimit =
    Number(body?.dailyLimit) > 0 ? Number(body.dailyLimit) : ASSIST_AI_DAILY_LIMIT;
  let remaining = body?.remaining;
  const usedRaw = body?.usedToday;
  if (remaining === undefined || remaining === null || remaining === '') {
    if (usedRaw !== undefined && usedRaw !== null && String(usedRaw).trim() !== '') {
      const u = Number(usedRaw);
      remaining = Number.isNaN(u) ? dailyLimit : Math.max(0, dailyLimit - u);
    } else {
      remaining = dailyLimit;
    }
  } else {
    const r = Number(remaining);
    remaining = Number.isNaN(r) ? dailyLimit : r;
  }
  const usedToday = Number(usedRaw);
  return {
    remaining,
    dailyLimit,
    usedToday: Number.isNaN(usedToday) ? Math.max(0, dailyLimit - remaining) : usedToday,
  };
}

const MODULES = [
  {
    id: 'workspace',
    name: '创作工作台',
    blurb: '作品与章节管理',
    icon: DashboardIcon,
    accent: '#22d3ee',
    glow: 'rgba(34, 211, 238, 0.45)',
  },
];

/** 与 Workspace「AI 生成大纲」弹窗：Paper、输入框描边与聚焦态一致 */
const ASSIST_DIALOG_PAPER = {
  /** assist-dialog-compact：覆盖 Workspace.css 里 character-panel-cyber 的 height:100%，避免弹窗被撑得过高 */
  className: 'character-panel-cyber assist-dialog-compact',
  sx: {
    bgcolor: 'rgba(10, 11, 18, 0.98)',
    backgroundImage: 'none',
    border: '1px solid rgba(255, 255, 255, 0.1)',
    borderRadius: 2,
    maxWidth: 420,
    width: { xs: 'min(calc(100vw - 24px), 420px)', sm: 420 },
    m: '12px auto',
    boxShadow: '0 0 50px rgba(0, 0, 0, 0.8), 0 0 100px rgba(34, 211, 238, 0.14)',
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
  },
};

const ASSIST_DIALOG_TITLE_SX = {
  display: 'flex',
  alignItems: 'center',
  gap: 1,
  py: 1.25,
  px: 2,
  flexShrink: 0,
  borderBottom: '1px solid rgba(255,255,255,0.1)',
  color: 'rgba(255,255,255,0.95) !important',
};

const assistDialogTextFieldSx = {
  '& .MuiOutlinedInput-root': {
    bgcolor: 'rgba(255,255,255,0.03)',
    color: 'rgba(255,255,255,0.92)',
    fontSize: 13,
    border: '1px solid rgba(255,255,255,0.1)',
    borderRadius: 1,
    '& fieldset': { border: 'none' },
    '&:hover': { borderColor: 'rgba(255,255,255,0.18)' },
    '&.Mui-focused': {
      borderColor: 'rgba(34, 211, 238, 0.55)',
      boxShadow: '0 0 0 1px rgba(34, 211, 238, 0.18)',
    },
  },
  '& .MuiInputLabel-root': {
    color: 'rgba(255,255,255,0.5)',
    fontSize: 12,
    '&.Mui-focused': { color: 'rgba(34, 211, 238, 0.95)' },
  },
  '& .MuiInputBase-input::placeholder': {
    color: 'rgba(255,255,255,0.28)',
    opacity: 1,
  },
};

const ToolsPage = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isNarrow = useMediaQuery(theme.breakpoints.down('md'));

  /** PC 与移动端统一：默认进入能力模块总览（四卡片），点「创作工作台」再进作品管理 */
  const [moduleHubOpen, setModuleHubOpen] = useState(true);

  const [allNovels, setAllNovels] = useState([]);
  const [loadingNovels, setLoadingNovels] = useState(false);
  const [creatingNew, setCreatingNew] = useState(false);

  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [newProjectTitle, setNewProjectTitle] = useState('');
  const [newProjectDesc, setNewProjectDesc] = useState('');

  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingNovel, setEditingNovel] = useState(null);
  const [editTitle, setEditTitle] = useState('');
  const [editDesc, setEditDesc] = useState('');

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deletingNovel, setDeletingNovel] = useState(null);

  const [anchorEl, setAnchorEl] = useState(null);
  const [menuNovel, setMenuNovel] = useState(null);

  /** 辅助创作每日剩余次数（公测 50 次/天） */
  const [assistQuota, setAssistQuota] = useState(null); // { usedToday, remaining, dailyLimit }
  const [assistQuotaLoading, setAssistQuotaLoading] = useState(false);
  const [usageDialogOpen, setUsageDialogOpen] = useState(false);
  const [usageLogs, setUsageLogs] = useState([]);
  const [usageLogsLoading, setUsageLogsLoading] = useState(false);
  const [usageLogsTotal, setUsageLogsTotal] = useState(0);

  const fetchAssistAiQuota = useCallback(async () => {
    setAssistQuotaLoading(true);
    try {
      const res = await apiClient.get('/assist/ai/quota');
      setAssistQuota(normalizeAssistQuotaPayload(res.data));
    } catch (e) {
      console.error('加载次数余额失败:', e);
      // 请求失败时仍显示公测默认满额，避免「—」；成功后会自动校正
      setAssistQuota({
        remaining: ASSIST_AI_DAILY_LIMIT,
        dailyLimit: ASSIST_AI_DAILY_LIMIT,
        usedToday: 0,
      });
    } finally {
      setAssistQuotaLoading(false);
    }
  }, []);

  const fetchUsageLogs = useCallback(async () => {
    setUsageLogsLoading(true);
    try {
      const res = await apiClient.get('/assist/ai/usage-logs', { params: { page: 1, limit: 50 } });
      setUsageLogs(res.data?.data || []);
      setUsageLogsTotal(res.data?.total ?? 0);
    } catch (e) {
      console.error('加载扣费记录失败:', e);
      setUsageLogs([]);
      setUsageLogsTotal(0);
    } finally {
      setUsageLogsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadAllNovels();
  }, []);

  useEffect(() => {
    fetchAssistAiQuota();
  }, [fetchAssistAiQuota]);

  useEffect(() => {
    if (usageDialogOpen) {
      fetchUsageLogs();
    }
  }, [usageDialogOpen, fetchUsageLogs]);

  useEffect(() => {
    const onRefresh = () => {
      fetchAssistAiQuota();
    };
    window.addEventListener('assistAiQuotaRefresh', onRefresh);
    return () => window.removeEventListener('assistAiQuotaRefresh', onRefresh);
  }, [fetchAssistAiQuota]);

  const loadAllNovels = async () => {
    setLoadingNovels(true);
    try {
      const response = await apiClient.get('/novels/user/me', {
        params: { creationType: 'assist' },
      });
      const list = response.data?.data || response.data || [];
      setAllNovels(list);
    } catch (e) {
      console.error('加载作品失败:', e);
      setAllNovels([]);
    } finally {
      setLoadingNovels(false);
    }
  };

  const openCreateDialog = () => {
    setNewProjectTitle('');
    setNewProjectDesc('');
    setCreateDialogOpen(true);
  };

  const confirmCreateAssistNovel = async () => {
    if (!newProjectTitle.trim()) return;
    setCreatingNew(true);
    setCreateDialogOpen(false);
    try {
      const res = await apiClient.post('/novels', {
        title: newProjectTitle.trim(),
        description: newProjectDesc.trim(),
        creationType: 'assist',
      });
      const data = res.data?.data || res.data;
      const id = data?.id || data?._id;
      if (id) {
        loadAllNovels();
        navigate(`/workspace/${id}`, { state: { from: '/tools' } });
      }
    } catch (e) {
      console.error('创建辅助写作作品失败:', e);
    } finally {
      setCreatingNew(false);
    }
  };

  const openEditDialog = (novel, e) => {
    e.stopPropagation();
    setAnchorEl(null);
    setEditingNovel(novel);
    setEditTitle(novel.title || '');
    setEditDesc(novel.description || '');
    setEditDialogOpen(true);
  };

  const confirmEditNovel = async () => {
    if (!editingNovel || !editTitle.trim()) return;
    try {
      const id = editingNovel._id || editingNovel.id;
      await apiClient.put(`/novels/${id}`, {
        title: editTitle.trim(),
        description: editDesc.trim(),
      });
      loadAllNovels();
      setEditDialogOpen(false);
      setEditingNovel(null);
    } catch (e) {
      console.error('更新作品失败:', e);
    }
  };

  const openDeleteDialog = (novel, e) => {
    e.stopPropagation();
    setAnchorEl(null);
    setDeletingNovel(novel);
    setDeleteDialogOpen(true);
  };

  const confirmDeleteNovel = async () => {
    if (!deletingNovel) return;
    try {
      const id = deletingNovel._id || deletingNovel.id;
      if (id.startsWith('assist_')) {
        await apiClient.delete(`/assist/novels/${id}`);
      } else {
        await apiClient.delete(`/novels/${id}`);
      }
      loadAllNovels();
      setDeleteDialogOpen(false);
      setDeletingNovel(null);
    } catch (e) {
      console.error('删除作品失败:', e);
    }
  };

  const openCardMenu = (event, novel) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
    setMenuNovel(novel);
  };

  const closeCardMenu = () => {
    setAnchorEl(null);
    setMenuNovel(null);
  };

  const goModule = useCallback(
    (id) => {
      if (id === 'workspace') {
        setModuleHubOpen(false);
        return;
      }
    },
    []
  );

  const workbenchVariants = {
    hidden: { opacity: 0 },
    show: {
      opacity: 1,
      transition: { staggerChildren: 0.09, delayChildren: 0.05 },
    },
  };

  const workbenchItem = {
    hidden: { opacity: 0, y: 18 },
    show: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.5, ease: [0.22, 1, 0.36, 1] },
    },
  };

  const renderWorkspaceBody = () => (
    <motion.div
      className="assist-workbench-root"
      variants={workbenchVariants}
      initial="hidden"
      animate="show"
      style={{ maxWidth: 1200, margin: '0 auto' }}
    >
      {/* 仅创作工作台：返回总览 + 标题，不再展示其它能力模块 */}
      <motion.div variants={workbenchItem}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: { xs: 1.25, sm: 2 },
            mb: 2.5,
          }}
        >
          <Tooltip title="返回能力总览">
            <motion.span
              whileHover={{ scale: 1.06 }}
              whileTap={{ scale: 0.94 }}
              style={{ display: 'inline-flex' }}
            >
              <IconButton
                size="small"
                onClick={() => setModuleHubOpen(true)}
                sx={{
                  color: 'rgba(255,255,255,0.88)',
                  border: '1px solid rgba(255,255,255,0.14)',
                  bgcolor: 'rgba(255,255,255,0.03)',
                  mt: 0.25,
                }}
              >
                <ArrowBackIcon fontSize="small" />
              </IconButton>
            </motion.span>
          </Tooltip>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, flexWrap: 'wrap', mb: 0.5 }}>
              <motion.div
                animate={{ rotate: [0, -6, 6, 0] }}
                transition={{ duration: 4, repeat: Infinity, ease: 'easeInOut' }}
                style={{ display: 'flex' }}
              >
                <Box
                  sx={{
                    width: 40,
                    height: 40,
                    borderRadius: '12px',
                    background: 'linear-gradient(135deg, rgba(34,211,238,0.28) 0%, rgba(251,191,36,0.18) 100%)',
                    border: '1px solid rgba(34,211,238,0.35)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxShadow: '0 0 24px rgba(34, 211, 238, 0.15)',
                  }}
                >
                  <DashboardIcon sx={{ fontSize: 22, color: '#22d3ee' }} />
                </Box>
              </motion.div>
              <Typography
                className="character-panel-title-glow"
                sx={{
                  fontSize: { xs: '11px !important', sm: '13px !important' },
                  letterSpacing: '0.14em !important',
                  mb: 0,
                }}
              >
                创作工作台
              </Typography>
            </Box>
            <Typography sx={{ color: 'rgba(255,255,255,0.42)', fontSize: { xs: '0.75rem', sm: '0.8rem' }, lineHeight: 1.55, pl: { xs: 0, sm: 0.5 } }}>
              在此管理作品列表；进入单个作品后可使用大纲生成、章纲与章节等能力。
            </Typography>
          </Box>
        </Box>
      </motion.div>

      <motion.div variants={workbenchItem}>
        <Box className="character-panel-card assist-workbench-intro" sx={{ mb: 2.5, borderColor: 'rgba(34, 211, 238, 0.28)' }}>
          <Typography className="character-panel-section-label" sx={{ mb: 1, color: '#22d3ee !important', fontSize: '11px !important' }}>
            ◆ 作品管理
          </Typography>
          <Typography sx={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.8rem', lineHeight: 1.65, mb: 0 }}>
            新建或打开作品。每个作品在独立工作台中编辑，数据与全自动写作项目相互隔离。
          </Typography>
        </Box>
      </motion.div>

      <motion.div variants={workbenchItem} whileHover={{ scale: creatingNew ? 1 : 1.008 }} transition={{ type: 'spring', stiffness: 400, damping: 24 }}>
        <Box
          onClick={openCreateDialog}
          role="button"
          className="assist-workbench-new-card"
          sx={{
            p: 2,
            mb: 2.5,
            borderRadius: 2,
            border: '1px dashed rgba(34, 211, 238, 0.5)',
            cursor: creatingNew ? 'wait' : 'pointer',
            opacity: creatingNew ? 0.6 : 1,
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            maxWidth: { xs: '100%', sm: 400 },
            background: 'linear-gradient(90deg, rgba(34,211,238,0.1) 0%, rgba(251,191,36,0.04) 45%, transparent 70%)',
            position: 'relative',
            overflow: 'hidden',
            '&:hover': {
              borderColor: 'rgba(34, 211, 238, 0.55)',
              boxShadow: '0 0 36px rgba(34, 211, 238, 0.14)',
            },
          }}
        >
          <motion.div
            animate={creatingNew ? {} : { scale: [1, 1.06, 1] }}
            transition={{ duration: 2.2, repeat: Infinity, ease: 'easeInOut' }}
          >
            <Box
              sx={{
                width: 44,
                height: 44,
                borderRadius: '14px',
                background: 'linear-gradient(135deg, rgba(251,191,36,0.35) 0%, rgba(34,211,238,0.32) 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <AddIcon sx={{ color: '#fff', fontSize: 24 }} />
            </Box>
          </motion.div>
          <Box>
            <Typography sx={{ color: 'rgba(255,255,255,0.92)', fontWeight: 700, fontSize: '0.95rem' }}>
              {creatingNew ? '创建中…' : '新建辅助写作作品'}
            </Typography>
            <Typography sx={{ color: 'rgba(255,255,255,0.45)', fontSize: '0.75rem' }}>从零开始，进入新版工作台</Typography>
          </Box>
          <ChevronRightIcon sx={{ ml: 'auto', color: 'rgba(255,255,255,0.25)' }} />
        </Box>
      </motion.div>

      {loadingNovels ? (
        <motion.div variants={workbenchItem}>
          <Box sx={{ textAlign: 'center', py: 6 }}>
            <motion.div
              animate={{ opacity: [0.4, 1, 0.4] }}
              transition={{ duration: 1.4, repeat: Infinity }}
            >
              <Typography sx={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.9rem' }}>加载作品列表…</Typography>
            </motion.div>
          </Box>
        </motion.div>
      ) : allNovels.length > 0 ? (
        <motion.div variants={workbenchItem}>
          <Box
            className="assist-workbench-grid"
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', lg: 'repeat(3, 1fr)' },
              gap: 2,
            }}
          >
          {allNovels.slice(0, 30).map((novel, index) => {
            const id = novel._id || novel.id;
            const isAssist = (novel.creationType || 'auto') === 'assist';
            return (
              <motion.div
                key={id}
                initial={{ opacity: 0, y: 22 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{
                  delay: 0.22 + index * 0.05,
                  duration: 0.45,
                  ease: [0.22, 1, 0.36, 1],
                }}
                whileHover={{ y: -4, transition: { type: 'spring', stiffness: 420, damping: 22 } }}
              >
                <Card
                  className="assist-novel-card"
                  sx={{
                    borderRadius: '16px',
                    overflow: 'hidden',
                    background: 'rgba(255,255,255,0.02)',
                    border: '1px solid rgba(255,255,255,0.1)',
                    height: '100%',
                    transition: 'box-shadow 0.35s ease, border-color 0.25s ease',
                    '&:hover': {
                      borderColor: 'rgba(34, 211, 238, 0.35)',
                      boxShadow: '0 20px 50px rgba(0,0,0,0.45), 0 0 40px rgba(34, 211, 238, 0.14)',
                    },
                  }}
                >
                  <Box sx={{ position: 'relative' }}>
                    {isAssist && (
                      <IconButton
                        size="small"
                        onClick={(e) => openCardMenu(e, novel)}
                        sx={{
                          position: 'absolute',
                          top: 8,
                          right: 8,
                          zIndex: 10,
                          bgcolor: 'rgba(10,11,18,0.85)',
                          border: '1px solid rgba(255,255,255,0.1)',
                        }}
                      >
                        <MoreVertIcon sx={{ fontSize: 18, color: 'rgba(255,255,255,0.65)' }} />
                      </IconButton>
                    )}
                    <CardActionArea
                      onClick={() => navigate(`/workspace/${id}`, { state: { from: '/tools' } })}
                      sx={{ borderRadius: '16px' }}
                    >
                      <CardContent sx={{ p: 2.5 }}>
                        <Typography
                          variant="h6"
                          sx={{
                            fontWeight: 700,
                            color: 'rgba(255,255,255,0.92)',
                            mb: 1,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {novel.title || '未命名作品'}
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{
                            color: 'rgba(255,255,255,0.45)',
                            fontSize: '0.8rem',
                            mb: 1.5,
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                            minHeight: '2.4em',
                          }}
                        >
                          {novel.description || '暂无简介'}
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                          <Chip
                            label={isAssist ? '辅助写作' : '全自动创作'}
                            size="small"
                            sx={{
                              bgcolor: isAssist ? 'rgba(34, 211, 238, 0.12)' : 'rgba(251, 113, 133, 0.12)',
                              color: isAssist ? '#22d3ee' : '#fb7185',
                              border: '1px solid rgba(255,255,255,0.08)',
                              fontWeight: 600,
                            }}
                          />
                          <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.35)' }}>
                            {novel.updatedAt ? new Date(novel.updatedAt).toLocaleDateString('zh-CN') : '-'}
                          </Typography>
                        </Box>
                      </CardContent>
                    </CardActionArea>
                  </Box>
                </Card>
              </motion.div>
            );
          })}
          </Box>
        </motion.div>
      ) : (
        <motion.div variants={workbenchItem} initial={{ scale: 0.97 }} animate={{ scale: 1 }} transition={{ type: 'spring', stiffness: 320, damping: 20 }}>
          <Box className="character-panel-card" sx={{ textAlign: 'center', py: 5 }}>
            <Typography sx={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.9rem' }}>暂无作品，点击上方新建开始创作</Typography>
          </Box>
        </motion.div>
      )}
    </motion.div>
  );

  const renderModuleHub = () => (
    <Box
      sx={{
        px: { xs: 2, sm: 3, md: 4 },
        py: { xs: 2, sm: 3, md: 4 },
        maxWidth: { xs: 520, sm: 560, md: 640 },
        mx: 'auto',
        width: '100%',
      }}
    >
      <motion.div initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}>
        <Typography className="character-panel-title-glow" sx={{ mb: 0.5, textAlign: 'center', fontSize: '12px !important' }}>
          辅助创作
        </Typography>
        <Typography sx={{ textAlign: 'center', color: 'rgba(255,255,255,0.45)', fontSize: '0.8rem', mb: 3 }}>
          选择能力模块，逐层进入
        </Typography>
      </motion.div>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        {MODULES.map((m, i) => {
          const Icon = m.icon;
          return (
            <motion.div
              key={m.id}
              initial={{ opacity: 0, x: -16 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.08 * i, duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
            >
              <Box
                component="button"
                type="button"
                onClick={() => goModule(m.id)}
                className="assist-hub-mobile-card"
                sx={{
                  width: '100%',
                  textAlign: 'left',
                  p: { xs: 2, md: 2.25 },
                  borderRadius: '16px',
                  border: '1px solid rgba(255,255,255,0.1)',
                  background: 'rgba(255,255,255,0.03)',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2,
                  position: 'relative',
                  overflow: 'hidden',
                  transition: 'border-color 0.3s ease, box-shadow 0.3s ease',
                  '&:hover': {
                    borderColor: m.accent,
                    boxShadow: `0 0 36px ${m.glow}`,
                  },
                }}
              >
                <Box
                  sx={{
                    width: { xs: 52, md: 56 },
                    height: { xs: 52, md: 56 },
                    borderRadius: '14px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    background: `linear-gradient(135deg, ${m.accent}33 0%, transparent 100%)`,
                    border: `1px solid ${m.accent}44`,
                  }}
                >
                  <Icon sx={{ fontSize: { xs: 26, md: 28 }, color: m.accent }} />
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography sx={{ color: 'rgba(255,255,255,0.92)', fontWeight: 800, fontSize: { xs: '1rem', md: '1.05rem' }, mb: 0.25 }}>{m.name}</Typography>
                  <Typography sx={{ color: 'rgba(255,255,255,0.45)', fontSize: { xs: '0.78rem', md: '0.82rem' } }}>{m.blurb}</Typography>
                </Box>
                <ChevronRightIcon sx={{ color: 'rgba(255,255,255,0.25)' }} />
              </Box>
            </motion.div>
          );
        })}
      </Box>
    </Box>
  );

  return (
    <Box className="tools-page-container assist-tools-redesign nexus-layout">
      <Box className="tools-header assist-tools-header">
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
          <Tooltip title="返回首页">
            <IconButton onClick={() => navigate('/dashboard')} sx={{ color: 'rgba(255,255,255,0.65)' }} size="small">
              <ArrowBackIcon />
            </IconButton>
          </Tooltip>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box
              sx={{
                width: 38,
                height: 38,
                borderRadius: '12px',
                background: 'linear-gradient(135deg, rgba(251,191,36,0.45) 0%, rgba(34,211,238,0.4) 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 0 24px rgba(34, 211, 238, 0.3)',
              }}
            >
              <AutoStoriesIcon sx={{ fontSize: 22, color: '#0a0b12' }} />
            </Box>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 800,
                background: 'linear-gradient(90deg, #fef9c3 0%, #22d3ee 45%, #38bdf8 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                fontSize: { xs: '1rem', sm: '1.15rem' },
              }}
            >
              灵犀笔阁
            </Typography>
          </Box>
          <Chip
            label="辅助创作"
            size="small"
            sx={{
              background: 'rgba(34, 211, 238, 0.12)',
              color: '#a5f3fc',
              border: '1px solid rgba(34, 211, 238, 0.4)',
              fontWeight: 600,
            }}
          />
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Tooltip title="辅助写作每日免费次数，次日 0 点重置">
            <Chip
              size="small"
              label={
                assistQuotaLoading
                  ? '今日剩余 …'
                  : `今日剩余 ${assistQuota?.remaining ?? ASSIST_AI_DAILY_LIMIT}/${assistQuota?.dailyLimit ?? ASSIST_AI_DAILY_LIMIT} 次`
              }
              sx={{
                background: 'rgba(251, 191, 36, 0.12)',
                color: '#fde68a',
                border: '1px solid rgba(251, 191, 36, 0.35)',
                fontWeight: 700,
                fontSize: '0.75rem',
                cursor: 'default',
              }}
            />
          </Tooltip>
          <Tooltip title="辅助创作扣费记录">
            <IconButton
              size="small"
              onClick={() => setUsageDialogOpen(true)}
              sx={{ color: 'rgba(255, 255, 255, 0.55)' }}
            >
              <ReceiptLongIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Avatar
            sx={{
              width: 32,
              height: 32,
              background: 'linear-gradient(135deg, #22d3ee 0%, #38bdf8 50%, #fbbf24 100%)',
              border: '1px solid rgba(255,255,255,0.15)',
              fontSize: 14,
            }}
          >
            U
          </Avatar>
        </Box>
      </Box>

      <Box className="assist-tools-body">
        <Box className="assist-tools-inner character-panel-cyber" sx={{ position: 'relative', borderRadius: { xs: 0, md: '20px' }, border: { md: '1px solid rgba(255,255,255,0.1)' }, overflow: 'hidden', minHeight: { xs: 'calc(100vh - 56px)', md: 'calc(100vh - 80px)' }, maxHeight: { md: 'calc(100vh - 80px)' } }}>
          <AnimatePresence mode="wait">
            {moduleHubOpen ? (
              <motion.div
                key="hub"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.25 }}
                style={{ minHeight: '100%' }}
              >
                {renderModuleHub()}
              </motion.div>
            ) : (
              <motion.div
                key="workspace"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.25 }}
                style={{ padding: isNarrow ? '16px 12px 32px' : '28px 32px 40px', minHeight: '100%', overflowY: 'auto' }}
              >
                {renderWorkspaceBody()}
              </motion.div>
            )}
          </AnimatePresence>
        </Box>
      </Box>

      <Dialog
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
        maxWidth={false}
        fullWidth={false}
        scroll="paper"
        TransitionComponent={Grow}
        transitionDuration={200}
        PaperProps={ASSIST_DIALOG_PAPER}
      >
        <DialogTitle className="character-panel-title-glow" sx={ASSIST_DIALOG_TITLE_SX}>
          <AutoFixHighIcon sx={{ color: 'rgba(255,230,200,0.95)', fontSize: 20, filter: 'drop-shadow(0 0 6px rgba(255,230,200,0.5))' }} />
          新建辅助写作作品
        </DialogTitle>
        <DialogContent sx={{ p: 2, pt: 1.75, pb: 1.25, display: 'flex', flexDirection: 'column', gap: 1.75 }}>
          <TextField
            autoFocus
            size="small"
            fullWidth
            label="作品名称"
            placeholder="请输入作品名称"
            value={newProjectTitle}
            onChange={(e) => setNewProjectTitle(e.target.value)}
            sx={assistDialogTextFieldSx}
          />
          <TextField
            size="small"
            fullWidth
            label="作品简介"
            placeholder="选填，可简要描述题材或设定"
            value={newProjectDesc}
            onChange={(e) => setNewProjectDesc(e.target.value)}
            multiline
            minRows={2}
            maxRows={5}
            sx={assistDialogTextFieldSx}
          />
        </DialogContent>
        <DialogActions
          sx={{
            px: 2,
            py: 1.25,
            gap: 1,
            borderTop: '1px solid rgba(255,255,255,0.08)',
            bgcolor: 'rgba(0,0,0,0.2)',
            justifyContent: 'flex-end',
          }}
        >
          <Button
            onClick={() => setCreateDialogOpen(false)}
            sx={{ color: 'rgba(255,255,255,0.55)', textTransform: 'none', transition: 'color 0.2s ease', '&:hover': { color: 'rgba(255,255,255,0.85)' } }}
          >
            取消
          </Button>
          <Button
            onClick={confirmCreateAssistNovel}
            disabled={!newProjectTitle.trim() || creatingNew}
            sx={{
              textTransform: 'none',
              fontWeight: 700,
              px: 2.25,
              py: 0.75,
              borderRadius: '10px',
              color: '#0a0b12',
              background: 'linear-gradient(135deg, #22d3ee 0%, #38bdf8 55%, #fbbf24 100%)',
              boxShadow: '0 0 22px rgba(34, 211, 238, 0.35)',
              transition: 'transform 0.2s ease, box-shadow 0.2s ease, filter 0.2s ease',
              '&:hover': {
                background: 'linear-gradient(135deg, #06b6d4 0%, #22d3ee 50%, #f59e0b 100%)',
                boxShadow: '0 0 32px rgba(34, 211, 238, 0.35)',
                transform: 'translateY(-1px)',
              },
              '&:disabled': { opacity: 0.5, transform: 'none' },
            }}
          >
            {creatingNew ? '创建中…' : '创建'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
        maxWidth={false}
        fullWidth={false}
        scroll="paper"
        TransitionComponent={Grow}
        transitionDuration={200}
        PaperProps={ASSIST_DIALOG_PAPER}
      >
        <DialogTitle className="character-panel-title-glow" sx={ASSIST_DIALOG_TITLE_SX}>
          <EditIcon sx={{ color: 'rgba(255,230,200,0.95)', fontSize: 20, filter: 'drop-shadow(0 0 6px rgba(255,230,200,0.5))' }} />
          编辑作品信息
        </DialogTitle>
        <DialogContent sx={{ p: 2, pt: 1.75, pb: 1.25, display: 'flex', flexDirection: 'column', gap: 1.75 }}>
          <TextField
            autoFocus
            size="small"
            fullWidth
            label="作品名称"
            value={editTitle}
            onChange={(e) => setEditTitle(e.target.value)}
            sx={assistDialogTextFieldSx}
          />
          <TextField
            size="small"
            fullWidth
            label="作品简介"
            value={editDesc}
            onChange={(e) => setEditDesc(e.target.value)}
            multiline
            minRows={2}
            maxRows={5}
            sx={assistDialogTextFieldSx}
          />
        </DialogContent>
        <DialogActions
          sx={{
            px: 2,
            py: 1.25,
            gap: 1,
            borderTop: '1px solid rgba(255,255,255,0.08)',
            bgcolor: 'rgba(0,0,0,0.2)',
            justifyContent: 'flex-end',
          }}
        >
          <Button
            onClick={() => setEditDialogOpen(false)}
            sx={{ color: 'rgba(255,255,255,0.55)', textTransform: 'none', '&:hover': { color: 'rgba(255,255,255,0.85)' } }}
          >
            取消
          </Button>
          <Button
            onClick={confirmEditNovel}
            disabled={!editTitle.trim()}
            sx={{
              textTransform: 'none',
              fontWeight: 700,
              px: 2.25,
              py: 0.75,
              borderRadius: '10px',
              color: '#0a0b12',
              background: 'linear-gradient(135deg, #22d3ee 0%, #38bdf8 55%, #fbbf24 100%)',
              boxShadow: '0 0 22px rgba(34, 211, 238, 0.35)',
              transition: 'transform 0.2s ease, box-shadow 0.2s ease',
              '&:hover': {
                background: 'linear-gradient(135deg, #06b6d4 0%, #22d3ee 50%, #f59e0b 100%)',
                boxShadow: '0 0 32px rgba(34, 211, 238, 0.35)',
                transform: 'translateY(-1px)',
              },
              '&:disabled': { opacity: 0.5, transform: 'none' },
            }}
          >
            保存
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        maxWidth={false}
        fullWidth={false}
        scroll="paper"
        TransitionComponent={Grow}
        transitionDuration={200}
        PaperProps={{
          ...ASSIST_DIALOG_PAPER,
          className: 'character-panel-cyber assist-dialog-compact',
          sx: {
            ...ASSIST_DIALOG_PAPER.sx,
            maxWidth: 380,
            width: { xs: 'min(calc(100vw - 24px), 380px)', sm: 380 },
          },
        }}
      >
        <DialogTitle className="character-panel-title-glow" sx={{ ...ASSIST_DIALOG_TITLE_SX, letterSpacing: '0.12em' }}>
          <DeleteIcon sx={{ color: 'rgba(248, 113, 113, 0.9)', fontSize: 20 }} />
          确认删除
        </DialogTitle>
        <DialogContent sx={{ p: 2, pt: 1, pb: 1.25 }}>
          <Typography sx={{ color: 'rgba(255,255,255,0.55)', fontSize: '0.875rem', lineHeight: 1.6 }}>
            确定要删除作品 &quot;{deletingNovel?.title}&quot; 吗？此操作不可恢复。
          </Typography>
        </DialogContent>
        <DialogActions
          sx={{
            px: 2,
            py: 1.25,
            gap: 1,
            borderTop: '1px solid rgba(255,255,255,0.08)',
            bgcolor: 'rgba(0,0,0,0.2)',
            justifyContent: 'flex-end',
          }}
        >
          <Button onClick={() => setDeleteDialogOpen(false)} sx={{ color: 'rgba(255,255,255,0.55)', textTransform: 'none' }}>
            取消
          </Button>
          <Button onClick={confirmDeleteNovel} color="error" variant="contained" sx={{ textTransform: 'none', borderRadius: '10px', px: 2 }}>
            删除
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={usageDialogOpen}
        onClose={() => setUsageDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        scroll="paper"
        TransitionComponent={Grow}
        transitionDuration={200}
        PaperProps={{
          className: 'character-panel-cyber assist-dialog-compact',
          sx: {
            bgcolor: 'rgba(10, 11, 18, 0.98)',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            borderRadius: 2,
            maxHeight: 'min(80vh, 520px)',
          },
        }}
      >
        <DialogTitle
          className="character-panel-title-glow"
          sx={{
            ...ASSIST_DIALOG_TITLE_SX,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 1,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <ReceiptLongIcon sx={{ color: 'rgba(251, 191, 36, 0.95)', fontSize: 22 }} />
            辅助创作扣费记录
          </Box>
          <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.4)', fontWeight: 500 }}>
            每日免费 {ASSIST_AI_DAILY_LIMIT} 次
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ p: 0, pt: 0 }}>
          {usageLogsLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
              <CircularProgress size={28} sx={{ color: 'rgba(34, 211, 238, 0.7)' }} />
            </Box>
          ) : usageLogs.length === 0 ? (
            <Box sx={{ px: 2, py: 4, textAlign: 'center' }}>
              <Typography sx={{ color: 'rgba(255,255,255,0.45)', fontSize: '0.875rem' }}>暂无扣费记录</Typography>
            </Box>
          ) : (
            <List dense sx={{ py: 0, maxHeight: 400, overflow: 'auto' }}>
              {usageLogs.map((row) => {
                // 从 metadata 中提取详细信息
                const meta = row.metadata || {};
                const novelName = meta.novelName || (row.description ? row.description.match(/【(.+?)】/)?.[1] : '');
                const chapterInfo = meta.chapterNumber ? `第${meta.chapterNumber}章` : (row.description ? row.description.match(/第(\d+)章/)?.[0] : '');
                const operationType = meta.operationType || '';

                return (
                <ListItem
                  key={row._id}
                  sx={{
                    alignItems: 'flex-start',
                    flexDirection: 'column',
                    borderBottom: '1px solid rgba(255,255,255,0.06)',
                    py: 1.25,
                    px: 2,
                  }}
                >
                  {/* 根据记录类型显示不同标签 */}
                  {(() => {
                    const logTypeLabel = {
                      'consumption': '扣费',
                      'recharge': '充值',
                      'admin': '管理员操作',
                      'free': '免费'
                    }[row.type] || '扣费';
                    const logTypeColor = {
                      'consumption': '#ff6b6b',
                      'recharge': '#00ff9f',
                      'admin': '#90caf9',
                      'free': '#ffd700'
                    }[row.type] || '#ff6b6b';
                    return (
                      <Chip
                        label={logTypeLabel}
                        size="small"
                        sx={{ mb: 0.5, bgcolor: `${logTypeColor}22`, color: logTypeColor, fontWeight: 600, fontSize: 10, height: 18 }}
                      />
                    );
                  })()}
                  <Typography sx={{ color: 'rgba(255,255,255,0.88)', fontSize: '0.82rem', lineHeight: 1.45, mb: 0.5 }}>
                    {row.description || '辅助创作 AI 调用'}
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, alignItems: 'center' }}>
                    {/* 项目名称 */}
                    {novelName && (
                      <Typography variant="caption" sx={{ color: 'rgba(34, 211, 238, 0.85)', fontWeight: 600 }}>
                        [项目] {novelName}
                      </Typography>
                    )}
                    {/* 章节号 */}
                    {chapterInfo && (
                      <Typography variant="caption" sx={{ color: 'rgba(251, 191, 36, 0.9)', fontWeight: 600 }}>
                        [章节] {chapterInfo}
                      </Typography>
                    )}
                    {/* 操作类型 */}
                    {operationType && (
                      <Typography variant="caption" sx={{ color: 'rgba(167, 139, 250, 0.85)', fontWeight: 500 }}>
                        [操作] {operationType}
                      </Typography>
                    )}
                    {/* 扣减次数 */}
                    <Typography variant="caption" sx={{ color: '#ff6b6b', fontWeight: 700 }}>
                      扣减 {row.delta != null ? Math.abs(row.delta) : 1} 次
                    </Typography>
                    <Typography variant="caption" sx={{ color: 'rgba(251, 191, 36, 0.9)', fontWeight: 700 }}>
                      剩余 {row.balanceAfter != null ? row.balanceAfter : '—'} 次
                    </Typography>
                    <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.35)' }}>
                      {row.createdAt ? new Date(row.createdAt).toLocaleString('zh-CN') : ''}
                    </Typography>
                  </Box>
                </ListItem>
              )})}
            </List>
          )}
          {!usageLogsLoading && usageLogsTotal > 0 && (
            <Typography variant="caption" sx={{ display: 'block', px: 2, pb: 1.5, color: 'rgba(255,255,255,0.35)' }}>
              共 {usageLogsTotal} 条（本页最多 50 条）
            </Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 2, py: 1.25, borderTop: '1px solid rgba(255,255,255,0.08)' }}>
          <Button
            onClick={() => {
              fetchUsageLogs();
              fetchAssistAiQuota();
            }}
            sx={{ color: '#22d3ee', textTransform: 'none' }}
          >
            刷新
          </Button>
          <Button onClick={() => setUsageDialogOpen(false)} sx={{ color: 'rgba(255,255,255,0.65)', textTransform: 'none' }}>
            关闭
          </Button>
        </DialogActions>
      </Dialog>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={closeCardMenu}
        PaperProps={{
          sx: {
            bgcolor: 'rgba(14, 15, 22, 0.98)',
            border: '1px solid rgba(255,255,255,0.1)',
            borderRadius: '12px',
          },
        }}
      >
        <MenuItem onClick={(e) => openEditDialog(menuNovel, e)}>
          <MenuItemIcon>
            <EditIcon sx={{ color: 'rgba(255,255,255,0.6)' }} />
          </MenuItemIcon>
          <ListItemText primary="编辑" sx={{ color: 'rgba(255,255,255,0.9)' }} />
        </MenuItem>
        <MenuItem onClick={(e) => openDeleteDialog(menuNovel, e)}>
          <MenuItemIcon>
            <DeleteIcon sx={{ color: '#f87171' }} />
          </MenuItemIcon>
          <ListItemText primary="删除" sx={{ color: '#f87171' }} />
        </MenuItem>
      </Menu>
    </Box>
  );
};

export default ToolsPage;
